import {
  AVATAR_CATALOG,
  AVATAR_IDS,
  DICE_SKINS,
  type AvatarId,
  type DiceSkin,
} from '@shared/game/types';

export interface Preferences {
  name: string;
  avatar: AvatarId;
  diceSkin: DiceSkin;
  sound: boolean;
  music: boolean;
  sfxVolume: number;
  musicVolume: number;
  animations: boolean;
}

const STORAGE_KEY = 'reino-tableros.profile.v1';
const FREE_AVATARS = AVATAR_CATALOG.filter((avatar) => !avatar.premium).map((avatar) => avatar.id);

function randomFreeAvatar(): AvatarId {
  return FREE_AVATARS[Math.floor(Math.random() * FREE_AVATARS.length)] ?? 'aurora';
}

export function defaultPreferences(): Preferences {
  return {
    name: `Viajero ${Math.floor(10 + Math.random() * 90)}`,
    avatar: randomFreeAvatar(),
    diceSkin: 'classic',
    sound: true,
    music: true,
    sfxVolume: 0.85,
    musicVolume: 0.55,
    animations: true,
  };
}

function clampVolume(value: unknown, fallback: number): number {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(0, Math.min(1, number));
}

export function loadPreferences(): Preferences {
  const fallback = defaultPreferences();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    const avatar = AVATAR_IDS.includes(parsed.avatar as AvatarId) ? (parsed.avatar as AvatarId) : fallback.avatar;
    const diceSkin = DICE_SKINS.includes(parsed.diceSkin as DiceSkin) ? (parsed.diceSkin as DiceSkin) : 'classic';
    // Migración: la música pasó a estar activa por defecto. Se enciende una vez
    // para quien venía de la versión silenciosa; después se respeta su elección.
    let music = parsed.music !== false;
    if (localStorage.getItem('altia.music.migrated.v1') !== '1') {
      music = true;
      localStorage.setItem('altia.music.migrated.v1', '1');
    }
    return {
      name: typeof parsed.name === 'string' && parsed.name.trim() ? parsed.name.trim().slice(0, 20) : fallback.name,
      avatar,
      diceSkin,
      sound: parsed.sound !== false,
      music,
      sfxVolume: clampVolume(parsed.sfxVolume, 0.85),
      musicVolume: clampVolume(parsed.musicVolume, 0.55),
      animations: parsed.animations !== false,
    };
  } catch {
    return fallback;
  }
}

export function savePreferences(preferences: Preferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // El navegador puede bloquear localStorage en modo privado; la app sigue funcionando en memoria.
  }
}

export function loadSessionToken(): string | null {
  try {
    return localStorage.getItem('reino-tableros.session.v1');
  } catch {
    return null;
  }
}

export function saveSessionToken(token: string): void {
  try {
    localStorage.setItem('reino-tableros.session.v1', token);
  } catch {
    // Sin persistencia, la reconexión tras recargar no estará disponible.
  }
}
